import { api } from '@/lib/api';

/**
 * Instagram Embedded Signup API Service
 *
 * Handles communication with backend for Instagram Direct Embedded Signup flow
 *
 * Flow:
 * 1. Frontend gets config (appId, configId) from backend
 * 2. Frontend loads Facebook SDK and calls FB.login() with Instagram permissions
 * 3. User authorizes in Facebook popup and selects Instagram Business account
 * 4. Frontend receives accessToken via FB.login response
 * 5. Frontend sends accessToken + pageId + instagramAccountId to backend
 * 6. Backend exchanges short-lived token for long-lived token and configures webhook
 */

/**
 * Configuration returned by backend for Facebook SDK initialization
 */
export interface InstagramEmbeddedSignupConfig {
  appId: string;           // Meta App ID for FB.init()
  configId: string;        // Instagram Embedded Signup Config ID for FB.login()
  graphApiVersion: string; // Graph API version (e.g., 'v21.0')
}

/**
 * Result from completing the Embedded Signup flow
 */
export interface InstagramEmbeddedSignupResult {
  accessToken: string;         // Long-lived user access token
  pageAccessToken: string;     // Page access token (used for Instagram Messaging API)
  instagramAccountId: string;  // Instagram Business Account ID
  pageId: string;              // Facebook Page ID linked to Instagram
  username?: string;           // Instagram username (@handle)
  webhookConfigured: boolean;  // Whether webhook was auto-configured
}

/**
 * Request to complete Embedded Signup
 */
export interface CompleteInstagramEmbeddedSignupRequest {
  accessToken: string;         // Short-lived access token from FB.login()
  instagramAccountId: string;  // Instagram Account ID from API response
  pageId: string;              // Page ID from API response
}

export const instagramApi = {
  /**
   * Get Instagram Embedded Signup configuration for frontend SDK
   *
   * Returns appId, configId, and graphApiVersion needed to:
   * 1. Initialize Facebook SDK with FB.init()
   * 2. Start Embedded Signup with FB.login()
   *
   * @returns InstagramEmbeddedSignupConfig
   */
  async getEmbeddedSignupConfig(): Promise<InstagramEmbeddedSignupConfig> {
    const response = await api.get('provider/instagram/embedded-signup-config');
    return response.data.data;
  },

  /**
   * Complete Instagram Embedded Signup flow
   *
   * After user authorizes in Facebook popup and we receive the code:
   * 1. Sends code to backend
   * 2. Backend exchanges code for access token
   * 3. Backend fetches Instagram Business accounts
   * 4. Backend configures webhook
   * 5. Returns credentials for channel creation
   *
   * @param data - Code, instagramAccountId, and pageId from authorization
   * @returns InstagramEmbeddedSignupResult with access token and config status
   */
  async completeEmbeddedSignup(
    data: CompleteInstagramEmbeddedSignupRequest
  ): Promise<InstagramEmbeddedSignupResult> {
    const response = await api.post('provider/instagram/embedded-signup/complete', data);
    return response.data.data;
  },

  /**
   * Get Instagram Business accounts linked to a Facebook page
   *
   * Called after FB.login() to let user select which Instagram account to connect
   *
   * @param accessToken - Short-lived access token from FB.login()
   * @returns List of Instagram Business accounts available
   */
  async getInstagramAccounts(accessToken: string): Promise<Array<{
    id: string;
    username: string;
    name: string;
    pageId: string;
    pageName: string;
  }>> {
    const response = await api.post('provider/instagram/accounts', {
      accessToken,
    });
    return response.data.data;
  },

};
